<template>
  <q-card
    flat
    bordered
    :class="['sequence-card compact cursor-pointer', lastInList ? '' : 'q-mb-xs']"
    @click.stop="onCardClick"
  >
    <!-- HEADER: id + created (no separate "Создано" label) -->
    <div class="row items-center justify-between q-px-sm q-pt-sm">
      <div class="row items-center no-wrap q-gutter-sm min-w-0">
        <div class="text-weight-medium">
          #{{ headerId }}
        </div>
        <div class="text-caption text-grey ellipsis">
          {{ formatDateTime(sequence.created) }}
        </div>
      </div>
      <q-badge
        dense
        :color="enriched._status.color"
      >
        {{ $t(enriched._status.labelKey) }}
      </q-badge>
    </div>

    <!-- MAIN: tasks vs orders -->
    <div class="row items-center justify-between q-px-sm q-pt-xs">
      <div class="text-body2">
        {{ $t('terminal.sequence.tasksLabel') }}
        {{ enriched._taskStats.complete }}/{{ enriched._taskStats.total }}
      </div>

      <div
        class="text-body2"
        :class="ordersInteractive ? 'text-primary cursor-pointer' : 'text-grey-7'"
        @click.stop="handleOrdersClick"
      >
        <template v-if="isSingleOrderDisplay">
          {{ $t('terminal.sequence.orderSingle', { id: singleOrderIdForDisplay }) }}
        </template>
        <template v-else>
          {{ $t('terminal.sequence.ordersMany', { n: ordersCountNum }) }}
        </template>
      </div>
    </div>

    <!-- PROGRESS -->
    <div class="q-px-sm q-mt-xs">
      <q-linear-progress
        :value="enriched._taskStats.progress"
        color="primary"
        track-color="grey-4"
        size="4px"
      />
    </div>

    <!-- LAST ACTION: absolute datetime (left) + relative hours/days (right) -->
    <div class="row items-center justify-between text-caption text-grey q-px-sm q-pb-sm q-mt-xs">
      <div class="ellipsis q-pr-xs">
        {{ $t('terminal.sequence.lastActionFullPrefix') }}
        {{ formatDateTime(sequence.updated) }}
      </div>
      <div class="text-right ellipsis q-pl-xs">
        {{ timeAgoHours(sequence.updated) }}
      </div>
    </div>

    <q-dialog v-model="ordersDialog">
      <q-card style="min-width: 280px; max-width: 90vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ $t('terminal.sequence.ordersDialogTitle') }}
          </div>
          <q-space />
          <q-btn
            v-close-popup
            flat
            dense
            no-caps
            :label="$t('Close')"
          />
        </q-card-section>
        <q-card-section class="q-pt-sm scroll" style="max-height: 60vh;">
          <div
            v-for="id in selectedOrders"
            :key="id"
            class="q-py-xs text-primary cursor-pointer"
            @click="openOrderInPanel(id)"
          >
            {{ $t('terminal.sequence.orderSingle', { id }) }}
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script>
import { enrichSequence } from '../../utils/sequence-wave-helpers'
import {
  formatDateTimeShort,
  timeAgoHoursFromRaw
} from '../../utils/terminal-datetime-format'

export default {
  name: 'TerminalSequenceCard',
  props: {
    sequence: {
      type: Object,
      required: true
    },
    showExtId: {
      type: Boolean,
      default: false
    },
    lastInList: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click'],
  data () {
    return {
      ordersDialog: false,
      selectedOrders: []
    }
  },
  computed: {
    enriched () {
      return enrichSequence(this.sequence)
    },
    headerId () {
      if (this.showExtId && this.sequence.extId) {
        return `${this.sequence.id}/${this.sequence.extId}`
      }
      return String(this.sequence.id)
    },
    ordersList () {
      const list = this.sequence.stats?.['orders-list']
      return Array.isArray(list) ? list : []
    },
    ordersCountNum () {
      const n = this.sequence.stats?.orders
      const num = Number(n)
      if (!Number.isNaN(num)) {
        return num
      }
      return this.ordersList.length
    },
    /** One order: stats says 1 and we have an id in the list */
    isSingleOrderDisplay () {
      return this.ordersCountNum === 1 && this.ordersList.length === 1
    },
    singleOrderIdForDisplay () {
      return this.isSingleOrderDisplay ? this.ordersList[0] : null
    },
    ordersInteractive () {
      return this.ordersCountNum > 0
    },
    appTimeZone () {
      const o = this.$appOptions || window.appOptions || {}
      return o.timezone || o.timeZone || o.locale?.timezone || this.$moment.tz.guess()
    }
  },
  methods: {
    /** HH:mm, D MMM in application timezone (created header + last action row). */
    formatDateTime (raw) {
      const loc = (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'en'
      return formatDateTimeShort(this.$moment, raw, this.appTimeZone, loc)
    },
    timeAgoHours (raw) {
      return timeAgoHoursFromRaw(raw, this.$t.bind(this))
    },
    handleOrdersClick () {
      if (!this.ordersInteractive) {
        return
      }
      if (this.isSingleOrderDisplay) {
        this.openOrderInPanel(this.singleOrderIdForDisplay)
        return
      }
      if (this.ordersCountNum <= 1) {
        return
      }
      this.selectedOrders = [...this.ordersList]
      this.ordersDialog = true
    },
    openOrderInPanel (orderId) {
      if (orderId == null || orderId === '') {
        return
      }
      const server = (window.appOptions && window.appOptions.defaultServer) || ''
      if (!server) {
        return
      }
      const base = server.replace(/\/$/, '')
      const path = `/#/outbound/orders/entity/Order/${orderId}`
      window.open(`${base}${path}`, '_blank', 'noopener,noreferrer')
    },
    onCardClick () {
      const s = this.sequence
      const payload = { ...s }
      delete payload._taskStats
      delete payload._status
      delete payload._taskRows
      this.$emit('click', payload)
    }
  }
}
</script>

<style scoped>
.sequence-card.compact {
  overflow: hidden;
}
</style>
